import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  Copy,
  FileText,
  ImageIcon,
  Paperclip,
  Undo2,
} from 'lucide-react';
import {
  ApiError,
  cash,
  type CashAccountDetail,
  type CashStatement,
  type CashStatementRow,
  type CashEntryAttachment,
} from '../api';
import { CashShell } from './CashShell';
import { useReadOnly } from '../ui/AppShell';
import { StickySummaryBar } from '../ui/mobileOps';
import { usePinGate } from '../auth/pinGate';
import { uploadFile } from '../lib/uploadFile';
import {
  Button,
  Checkbox,
  EmptyState,
  ErrorBanner,
  Pill,
  Select,
  Spinner,
  TextArea,
  TextInput,
} from '../ui/primitives';
import { SectionCard } from '../ui/components';
import { formatInr, toneFromBalance, todayIso, addDaysIso, buildCashCaption } from './format';
import { withCacheBust } from '../ledger/exportUrl';

export function CashAccountLedgerPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const accountId = Number(idParam);
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const { withPin } = usePinGate();

  const today = useMemo(() => todayIso(), []);
  const [from, setFrom] = useState(addDaysIso(today, -30));
  const [to, setTo] = useState(today);
  const [includeReversed, setIncludeReversed] = useState(false);
  const [entryOpen, setEntryOpen] = useState<null | 'in' | 'out'>(null);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const accountQ = useQuery<CashAccountDetail>({
    queryKey: ['cash-account', accountId],
    queryFn: () => cash.getAccount(accountId),
    enabled: Number.isFinite(accountId),
  });
  const ledgerQ = useQuery<CashStatement>({
    queryKey: ['cash-ledger', accountId, from, to, includeReversed],
    queryFn: () => cash.getLedger(accountId, { from, to, includeReversed }),
    enabled: Number.isFinite(accountId) && !!from && !!to,
  });

  // One-shot recent-activity fallback (mirrors the statement export): if the
  // default last-30-days window is empty, widen to the last year so a quiet
  // account shows its recent entries instead of a blank table. Manual date
  // edits disable it.
  const rangeTouchedRef = useRef(false);
  useEffect(() => {
    if (rangeTouchedRef.current) return;
    if (!ledgerQ.data || ledgerQ.data.rows.length > 0) return;
    rangeTouchedRef.current = true; // one shot, whatever we decide
    const floor = addDaysIso(today, -365);
    if (floor < from) setFrom(floor);
  }, [ledgerQ.data, from, today]);

  const attachmentQueries = useQueries({
    queries: (ledgerQ.data?.rows ?? []).map((row) => ({
      queryKey: ['cash-entry-attachments', row.id],
      queryFn: () => cash.listAttachments(row.id),
    })),
  });
  const attachmentsByEntry = useMemo(() => {
    const map = new Map<number, CashEntryAttachment[]>();
    (ledgerQ.data?.rows ?? []).forEach((row, index) => {
      const data = attachmentQueries[index]?.data;
      if (data) map.set(row.id, data);
    });
    return map;
  }, [attachmentQueries, ledgerQ.data]);

  const voidMut = useMutation({
    mutationFn: ({ entryId, reason }: { entryId: number; reason: string }) =>
      cash.voidEntry(entryId, reason),
    onSuccess: () => void qc.invalidateQueries(),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const uploadMut = useMutation({
    mutationFn: ({ entryId, file }: { entryId: number; file: File }) =>
      uploadFile<{ attachment: { id: number } }>(`/api/cash/entries/${entryId}/attachments`, file),
    onSuccess: (_, { entryId }) =>
      void qc.invalidateQueries({ queryKey: ['cash-entry-attachments', entryId] }),
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  if (!Number.isFinite(accountId)) {
    return (
      <CashShell title="Cash ledger">
        <SectionCard section="cash">
          <ErrorBanner>Not a valid account id.</ErrorBanner>
        </SectionCard>
      </CashShell>
    );
  }

  const name = accountQ.data?.account.name ?? '…';
  const balance = accountQ.data?.balance ?? '0';
  const caption = accountQ.data ? buildCashCaption(balance) : null;
  const rowCount = ledgerQ.data?.rows.length ?? 0;

  async function copyCaption(): Promise<void> {
    if (!caption) return;
    try {
      await navigator.clipboard.writeText(caption);
      setCaptionCopied(true);
      window.setTimeout(() => setCaptionCopied(false), 2000);
    } catch {
      // clipboard unavailable
    }
  }

  const pdfUrl = withCacheBust(`/api/cash/accounts/${accountId}/statement.pdf?from=${from}&to=${to}&include_reversed=${includeReversed}`);
  const pngUrl = withCacheBust(`/api/cash/accounts/${accountId}/statement.png?from=${from}&to=${to}&include_reversed=${includeReversed}`);

  return (
    <CashShell
      title={name}
      subtitle="Plain rupee statement with running balance and receipts"
      back={{ to: '/cash/accounts', label: 'People' }}
      actions={
        !readOnly ? (
          <>
            <Button size="sm" className="hidden md:inline-flex" onClick={() => { setError(null); setEntryOpen('in'); }}>
              <ArrowDownToLine className="h-3.5 w-3.5" strokeWidth={2} />
              <span>Money in</span>
            </Button>
            <Button size="sm" variant="secondary" className="hidden md:inline-flex" onClick={() => { setError(null); setEntryOpen('out'); }}>
              <ArrowUpFromLine className="h-3.5 w-3.5" strokeWidth={2} />
              <span>Money out</span>
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(280px,0.9fr)]">
        <div className="order-2 space-y-4 lg:order-1">
          {error && <ErrorBanner>{error}</ErrorBanner>}

          <div className="md:hidden">
            <StickySummaryBar
              section="cash"
              stackedBelowTabs
              title={name}
              subtitle="Use this ledger for the next cash movement."
              metrics={[
                { label: 'Balance', value: formatInr(balance), tone: metricTone(balance) },
                { label: 'Rows', value: String(rowCount) },
                { label: 'View', value: includeReversed ? 'All' : 'Live' },
              ]}
              actions={
                !readOnly ? (
                  <>
                    <Button size="sm" onClick={() => { setError(null); setEntryOpen('in'); }}>
                      In
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => { setError(null); setEntryOpen('out'); }}>
                      Out
                    </Button>
                  </>
                ) : undefined
              }
            />
          </div>

          <SectionCard section="cash" title="View and export">
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <TextInput
                  label="From"
                  type="date"
                  value={from}
                  onChange={(e) => {
                    rangeTouchedRef.current = true;
                    setFrom(e.target.value);
                  }}
                />
                <TextInput
                  label="To"
                  type="date"
                  value={to}
                  onChange={(e) => {
                    rangeTouchedRef.current = true;
                    setTo(e.target.value);
                  }}
                />
              </div>
              <Checkbox
                label="Show reversed entries"
                hint="Include voided rows and reversal history in the list below."
                checked={includeReversed}
                onChange={setIncludeReversed}
              />
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex min-h-[40px] items-center gap-1.5 rounded-[14px] border border-border/70 bg-surface px-3.5 py-2 text-small font-medium text-ink shadow-sm backdrop-blur hover:bg-surface-raised"
                >
                  <FileText className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} />
                  PDF
                </a>
                <a
                  href={pngUrl}
                  download={`cashbook-${accountId}-${from}-${to}.png`}
                  className="inline-flex min-h-[40px] items-center gap-1.5 rounded-[14px] border border-border/70 bg-surface px-3.5 py-2 text-small font-medium text-ink shadow-sm backdrop-blur hover:bg-surface-raised"
                >
                  <ImageIcon className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} />
                  PNG
                </a>
              </div>
            </div>
          </SectionCard>

          <SectionCard section="cash" title="Ledger" padded={false}>
            {ledgerQ.isLoading ? (
              <div className="flex items-center gap-2 px-4 py-3 text-small text-ink-muted">
                <Spinner /> loading…
              </div>
            ) : ledgerQ.data ? (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="min-w-full text-small">
                    <thead className="bg-surface-raised text-caption uppercase tracking-wide text-ink-muted">
                      <tr>
                        <th className="px-4 py-2.5 text-left">Date</th>
                        <th className="px-4 py-2.5 text-left">Description</th>
                        <th className="px-4 py-2.5 text-right">Out</th>
                        <th className="px-4 py-2.5 text-right">In</th>
                        <th className="px-4 py-2.5 text-right">Balance</th>
                        <th className="px-4 py-2.5 text-left">Receipt</th>
                        {!readOnly && <th className="px-4 py-2.5" />}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      <tr className="bg-amber-50/60">
                        <td className="px-4 py-2.5 text-amber-700">{from}</td>
                        <td className="px-4 py-2.5 font-medium text-amber-700">Opening balance</td>
                        <td className="px-4 py-2.5" />
                        <td className="px-4 py-2.5" />
                        <td className="px-4 py-2.5 text-right font-mono font-semibold text-amber-700">
                          {formatInr(ledgerQ.data.openingBalanceAsOfFrom)}
                        </td>
                        <td className="px-4 py-2.5" />
                        {!readOnly && <td className="px-4 py-2.5" />}
                      </tr>
                      {ledgerQ.data.rows.map((row) => (
                        <CashRow
                          key={row.id}
                          row={row}
                          attachments={attachmentsByEntry.get(row.id) ?? []}
                          readOnly={readOnly}
                          uploading={uploadMut.isPending && uploadMut.variables?.entryId === row.id}
                          onAttach={(file) => uploadMut.mutate({ entryId: row.id, file })}
                          onVoid={(reason) =>
                            void withPin(() => voidMut.mutateAsync({ entryId: row.id, reason }))
                          }
                        />
                      ))}
                      {ledgerQ.data.rows.length === 0 && (
                        <tr>
                          <td colSpan={readOnly ? 6 : 7} className="px-4 py-4 text-center text-small text-ink-muted">
                            No entries in this range.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-border bg-surface-raised">
                        <td className="px-4 py-2.5 font-semibold text-ink" colSpan={4}>
                          Closing balance ({to})
                        </td>
                        <td className={`px-4 py-2.5 text-right font-mono font-semibold ${toneFromBalance(ledgerQ.data.closingBalance)}`}>
                          {formatInr(ledgerQ.data.closingBalance)}
                        </td>
                        <td colSpan={readOnly ? 1 : 2} />
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="space-y-3 p-3 md:hidden">
                  <BalanceMarkerCard
                    title="Opening balance"
                    date={from}
                    value={formatInr(ledgerQ.data.openingBalanceAsOfFrom)}
                    tone="warn"
                  />
                  {ledgerQ.data.rows.map((row) => (
                    <CashLedgerMobileCard
                      key={row.id}
                      row={row}
                      attachments={attachmentsByEntry.get(row.id) ?? []}
                      readOnly={readOnly}
                      uploading={uploadMut.isPending && uploadMut.variables?.entryId === row.id}
                      onAttach={(file) => uploadMut.mutate({ entryId: row.id, file })}
                      onVoid={(reason) =>
                        void withPin(() => voidMut.mutateAsync({ entryId: row.id, reason }))
                      }
                    />
                  ))}
                  {ledgerQ.data.rows.length === 0 && (
                    <p className="py-4 text-center text-small text-ink-muted">No entries in this range.</p>
                  )}
                  <BalanceMarkerCard
                    title={`Closing balance (${to})`}
                    value={formatInr(ledgerQ.data.closingBalance)}
                    tone={balance.startsWith('-') ? 'neg' : balance === '0' ? 'default' : 'pos'}
                  />
                </div>
              </>
            ) : (
              <div className="p-3">
                <EmptyState section="cash" icon={FileText} title="No ledger data" description="Adjust the date range above." />
              </div>
            )}
          </SectionCard>
        </div>

        <aside className="order-1 hidden flex-col gap-4 lg:order-2 lg:flex">
          <SectionCard section="cash" title="Current balance">
            <p className={`font-mono text-display font-bold tabular-nums ${toneFromBalance(balance)}`}>
              {formatInr(balance)}
            </p>
            {caption && (
              <button
                type="button"
                onClick={() => void copyCaption()}
                title="Copy a shareable one-liner of the current balance"
                className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-caption font-medium text-ink transition-colors hover:bg-surface-raised"
              >
                {captionCopied ? (
                  <span className="text-pos">Copied “{caption}”</span>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" strokeWidth={2} />
                    Copy “{caption}”
                  </>
                )}
              </button>
            )}
            <p className="mt-1 text-caption text-ink-muted">
              Plain rupees. Positive = they owe you; negative = you owe them.
            </p>
            {!readOnly && (
              <div className="mt-4 flex flex-col gap-2">
                <Button onClick={() => { setError(null); setEntryOpen('in'); }}>
                  Money in
                </Button>
                <Button variant="secondary" onClick={() => { setError(null); setEntryOpen('out'); }}>
                  Money out
                </Button>
              </div>
            )}
          </SectionCard>
        </aside>
      </div>

      {entryOpen && (
        <EntryDialog
          accountId={accountId}
          direction={entryOpen}
          onClose={() => setEntryOpen(null)}
          onCreated={() => {
            setEntryOpen(null);
            void qc.invalidateQueries();
          }}
        />
      )}
    </CashShell>
  );
}

function CashLedgerMobileCard({
  row,
  attachments,
  readOnly,
  uploading,
  onAttach,
  onVoid,
}: {
  row: CashStatementRow;
  attachments: CashEntryAttachment[];
  readOnly: boolean;
  uploading: boolean;
  onAttach: (file: File) => void;
  onVoid: (reason: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const reversed = row.reversesEntryId !== null;
  const directionLabel = row.direction === 'in' ? 'Money received' : 'Money paid';

  return (
    <div
      className={[
        'overflow-hidden rounded-[22px] border border-border/70 app-shell-surface-soft p-4 shadow-sm backdrop-blur',
        reversed ? 'opacity-70' : '',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-small font-semibold tracking-[-0.01em] text-ink">
              {row.note ?? directionLabel}
            </div>
            {reversed && <Pill tone="neutral">Reversed</Pill>}
          </div>
          <div className="mt-1 text-[12px] text-ink-muted">{row.effectiveDate}</div>
          <div className="mt-2 text-[12px] text-ink-muted">{directionLabel}</div>
        </div>
        <div className="shrink-0 text-right">
          <div className={`font-mono text-[1rem] font-semibold tabular-nums ${row.direction === 'in' ? 'text-pos' : 'text-warn'}`}>
            {row.direction === 'in' ? '+' : '-'}{formatInr(row.amount)}
          </div>
          <div className="mt-1 text-[11px] uppercase tracking-[0.14em] text-ink-muted">
            bal {formatInr(row.runningBalance)}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border/70 pt-3">
        {attachments.length > 0 ? (
          <a
            href={`/api/attachments/${attachments[0]!.sha256}`}
            target="_blank"
            rel="noopener"
            className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-border/70 bg-surface px-3 py-1.5 text-caption font-medium text-ink hover:bg-surface-raised"
          >
            <Paperclip className="h-3.5 w-3.5 text-ink-muted" strokeWidth={2} />
            {attachments.length} file{attachments.length > 1 ? 's' : ''}
          </a>
        ) : !readOnly && !reversed ? (
          <>
            <input
              ref={inputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onAttach(file);
                if (inputRef.current) inputRef.current.value = '';
              }}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="inline-flex min-h-[36px] items-center gap-1.5 rounded-full border border-dashed border-border px-3 py-1.5 text-caption font-medium text-ink-muted hover:border-sec-cash hover:text-sec-cash disabled:opacity-60"
            >
              {uploading ? <Spinner /> : <><Paperclip className="h-3.5 w-3.5" strokeWidth={2} /> Add receipt</>}
            </button>
          </>
        ) : (
          <span className="text-caption text-ink-muted">No receipt</span>
        )}

        {!readOnly && !reversed && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              const reason = prompt('Reason for voiding this entry?');
              if (reason && reason.trim().length > 0) onVoid(reason.trim());
            }}
          >
            <Undo2 className="h-3.5 w-3.5" strokeWidth={2} />
            Void
          </Button>
        )}
      </div>
    </div>
  );
}

function BalanceMarkerCard({
  title,
  date,
  value,
  tone,
}: {
  title: string;
  date?: string;
  value: string;
  tone: 'default' | 'pos' | 'neg' | 'warn';
}) {
  return (
    <div className="overflow-hidden rounded-[22px] border border-border/70 app-shell-surface px-4 py-3 shadow-sm backdrop-blur">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-small font-semibold text-ink">{title}</div>
          {date && <div className="mt-1 text-[12px] text-ink-muted">{date}</div>}
        </div>
        <div className={[
          'font-mono text-small font-semibold tabular-nums',
          tone === 'neg' ? 'text-neg' : tone === 'pos' ? 'text-pos' : tone === 'warn' ? 'text-warn' : 'text-ink',
        ].join(' ')}>
          {value}
        </div>
      </div>
    </div>
  );
}

function CashRow({
  row,
  attachments,
  readOnly,
  uploading,
  onAttach,
  onVoid,
}: {
  row: CashStatementRow;
  attachments: CashEntryAttachment[];
  readOnly: boolean;
  uploading: boolean;
  onAttach: (file: File) => void;
  onVoid: (reason: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const reversed = row.reversesEntryId !== null;
  return (
    <tr className={reversed ? 'text-ink-muted line-through' : 'text-ink'}>
      <td className="px-4 py-2.5">{row.effectiveDate}</td>
      <td className="px-4 py-2.5">{row.note ?? (row.direction === 'out' ? 'Money out' : 'Money in')}</td>
      <td className="px-4 py-2.5 text-right font-mono text-warn">{row.direction === 'out' ? formatInr(row.amount) : ''}</td>
      <td className="px-4 py-2.5 text-right font-mono text-pos">{row.direction === 'in' ? formatInr(row.amount) : ''}</td>
      <td className="px-4 py-2.5 text-right font-mono">{formatInr(row.runningBalance)}</td>
      <td className="px-4 py-2.5">
        {attachments.length > 0 ? (
          <a href={`/api/attachments/${attachments[0]!.sha256}`} target="_blank" rel="noopener" className="text-caption text-sec-cash hover:underline">
            {attachments.length} file{attachments.length > 1 ? 's' : ''}
          </a>
        ) : !readOnly && !reversed ? (
          <>
            <input
              ref={inputRef}
              type="file"
              accept=".jpg,.jpeg,.png,.pdf"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onAttach(file);
                if (inputRef.current) inputRef.current.value = '';
              }}
            />
            <button
              type="button"
              disabled={uploading}
              onClick={() => inputRef.current?.click()}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-1.5 py-0.5 text-caption text-ink-muted transition-colors hover:border-sec-cash/60 hover:text-sec-cash disabled:opacity-60"
            >
              {uploading ? <Spinner /> : <><Paperclip className="h-3 w-3" strokeWidth={2} /> Add</>}
            </button>
          </>
        ) : (
          <span className="text-caption text-ink-muted">—</span>
        )}
      </td>
      {!readOnly && (
        <td className="px-4 py-2.5 text-right">
          {!reversed && (
            <button
              className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-caption font-medium text-neg transition-colors hover:bg-neg/5"
              onClick={() => {
                const reason = prompt('Reason for voiding this entry?');
                if (reason && reason.trim().length > 0) onVoid(reason.trim());
              }}
            >
              <Undo2 className="h-3.5 w-3.5" strokeWidth={2} /> Void
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

function EntryDialog({
  accountId,
  direction,
  onClose,
  onCreated,
}: {
  accountId: number;
  direction: 'in' | 'out';
  onClose: () => void;
  onCreated: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [effectiveDate, setEffectiveDate] = useState(() => todayIso());
  const [note, setNote] = useState('');
  const [dir, setDir] = useState<'in' | 'out'>(direction);
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      cash.createEntry({
        cashAccountId: accountId,
        direction: dir,
        amount: amount.trim(),
        effectiveDate,
        note: note.trim() || null,
      }),
    onSuccess: () => onCreated(),
    onError: (e) => setError(e instanceof ApiError ? e.message : String(e)),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (amount.trim().length === 0) {
      setError('Amount is required.');
      return;
    }
    createMut.mutate();
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-ink/50 backdrop-blur-sm sm:items-center sm:p-4">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-t-[28px] border border-border bg-surface shadow-elevated sm:rounded-[26px]">
        <header className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-base font-semibold text-ink">
            {dir === 'in' ? 'Money in (received)' : 'Money out (paid)'}
          </h2>
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </header>
        <form className="overflow-y-auto p-4" onSubmit={onSubmit}>
          <div className="space-y-3">
            <Select
              label="Direction"
              value={dir}
              onChange={(value) => setDir(value as 'in' | 'out')}
              options={[
                { value: 'out', label: 'Money out — you paid them' },
                { value: 'in', label: 'Money in — you received from them' },
              ]}
            />
            <TextInput
              label="Amount (₹)"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              name="amount"
              placeholder="e.g. 5000"
              inputMode="decimal"
              autoFocus
            />
            <TextInput
              label="Date"
              type="date"
              value={effectiveDate}
              onChange={(e) => setEffectiveDate(e.target.value)}
            />
            <TextArea
              label="Note (optional)"
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. UPI to Manoj"
            />
          </div>
          {error && <div className="mt-3"><ErrorBanner>{error}</ErrorBanner></div>}
          <div className="mt-4 flex items-center gap-2">
            <Button type="submit" disabled={createMut.isPending}>
              {createMut.isPending ? <Spinner /> : 'Save'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose} disabled={createMut.isPending}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function metricTone(balance: string): 'default' | 'pos' | 'neg' {
  if (balance.startsWith('-')) return 'neg';
  if (balance === '0') return 'default';
  return 'pos';
}
